import React, { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useQuery } from '@apollo/client';
import Button from '../../components/Button';
import Card, { Cards } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import FloatingCard from '../../components/FloatingCard';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import Popover from '../../components/Popover';
import SearchInput from '../../components/SearchInput';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import filterIcon from '../../icons/non-prefilled/16x16/filter.svg';
import { InviteButton } from '../../organization/InviteButton';
import SettingsContainerView from '../../settings/SettingsContainerView';
import nonEmpty from '../../utils/nonEmpty';
import TeammatesFilter from './TeammatesFilter';
import TeammatesRow from './TeammatesRow';
import * as styles from './TeammatesView.module.scss';
import {
  getInvitationsFilter,
  getSearchFilter,
  getUsersFilter,
} from './getFilters';
import useTeammatesFilter from './useTeammatesFilter';

const TEAMMATES_USER_FRAGMENT = gql(`
 fragment TeammatesUser on User {
    id
    ...TeammatesRow_user
  }
`);

type TeammatesUserFragment = DocumentType<typeof TEAMMATES_USER_FRAGMENT>;

const TEAMMATES_INVITATION_FRAGMENT = gql(`
 fragment TeammatesInvitation on Invitation {
    id
    ...TeammatesRow_invitation
  }
`);

type TeammatesInvitationFragment = DocumentType<
  typeof TEAMMATES_INVITATION_FRAGMENT
>;

const TEAMMATES_QUERY = gql(/** @complexity 15 */ `
  query Teammates {
    session: currentSession {
      organization {
        id
        users(includeDeactivated: true) {
          ...TeammatesUser
        }
        invitations {
          ...TeammatesInvitation
        }
        settings {
          requireInviteApproval
        }
      }
    }
  }
`);

const LIMIT_STEP = 30;

interface TeammatesProps {
  search?: string;
}

const Teammates = ({ search }: TeammatesProps) => {
  const { data } = useQuery(TEAMMATES_QUERY);
  const [limit, setLimit] = useState(LIMIT_STEP);

  const isRequiringInviteApproval =
    data?.session?.organization?.settings?.requireInviteApproval ?? false;

  const { types, statuses, reset } = useTeammatesFilter();

  const [searchQuery, setSearchQuery] = useState<string>(search ?? '');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 200);

  const { teammates, searchedTeammatesCount, allTeammatesCount } = useMemo<{
    teammates: (TeammatesUserFragment | TeammatesInvitationFragment)[];
    searchedTeammatesCount: number;
    allTeammatesCount: number;
  }>(() => {
    const users = data?.session?.organization?.users ?? [];
    const invitations = data?.session?.organization?.invitations ?? [];

    const searchedUsers = users.filter(getSearchFilter(debouncedSearchQuery));

    const filteredUsers = searchedUsers.filter(getUsersFilter(types, statuses));

    const searchedInvitations = invitations.filter(
      getSearchFilter(debouncedSearchQuery),
    );

    const filteredInvitations = searchedInvitations.filter(
      getInvitationsFilter(types, statuses),
    );

    const allUsers = [...filteredUsers, ...filteredInvitations].filter(
      nonEmpty,
    );

    return {
      teammates: allUsers,
      searchedTeammatesCount: debouncedSearchQuery
        ? searchedUsers.length + searchedInvitations.length
        : 0,
      allTeammatesCount: users.length + invitations.length,
    };
  }, [
    data?.session?.organization?.invitations,
    data?.session?.organization?.users,
    types,
    statuses,
    debouncedSearchQuery,
  ]);

  useEffect(() => {
    setLimit(LIMIT_STEP);
  }, [teammates.length]);

  const numberOfFilters = types.length + statuses.length;

  return (
    <SettingsContainerView
      title="Teammates"
      titleRight={<InviteButton />}
      className="admin-members-view"
      subtitle={
        <div className={styles.subtitleWrapper}>
          <h3 className={styles.subtitle}>{teammates.length} total</h3>
          <div className={styles.subtitleSearch}>
            <Popover
              placement="bottom-end"
              view={
                <FloatingCard noArrow>
                  <TeammatesFilter
                    requireInviteApproval={isRequiringInviteApproval}
                  />
                </FloatingCard>
              }
            >
              <Button
                className={styles.filterButton}
                variant="secondary"
                icon={filterIcon}
              >
                Filter{numberOfFilters > 0 ? ` (${numberOfFilters})` : ''}
              </Button>
            </Popover>
            <div className={styles.searchWrapper}>
              <SearchInput
                onChange={(e) => setSearchQuery(e.currentTarget.value)}
                value={searchQuery}
                placeholder="Search"
              />
            </div>
          </div>
        </div>
      }
    >
      <Cards>
        {teammates.length > 0 && (
          <>
            {teammates.slice(0, limit).map((teammate) => (
              <Card key={teammate.id} noPadding>
                {teammate.__typename === 'User' && (
                  <TeammatesRow user={teammate} />
                )}
                {teammate.__typename === 'Invitation' && (
                  <TeammatesRow invitation={teammate} />
                )}
              </Card>
            ))}
            {teammates.length < searchedTeammatesCount && (
              <div className={styles.showAllResultsText}>
                Some results exist outside of your filtering criteria.{' '}
                <Button onClick={reset} variant="link">
                  Show all {searchedTeammatesCount} results
                </Button>
              </div>
            )}
          </>
        )}
        {teammates.length === 0 && (
          <div className={styles.emptyContainer}>
            <EmptyState
              imageName="notFound"
              title="No results found"
              message="Some results exist outside of your filtering criteria."
              action={
                <Button onClick={reset} variant="secondary">
                  Show all{' '}
                  {searchedTeammatesCount > 0
                    ? searchedTeammatesCount
                    : allTeammatesCount}{' '}
                  results
                </Button>
              }
            />
          </div>
        )}

        <InfiniteScrollWaypoint
          fetchMore={
            limit >= teammates.length
              ? null
              : () => setLimit(limit + LIMIT_STEP)
          }
        />
      </Cards>
    </SettingsContainerView>
  );
};

export default Teammates;
